/**
 * CardBrowser — перегляд і редагування всіх карток у базі.
 *
 * Це «，知识» без повторення: пошук, фільтри, створення картки руками,
 * редагування, видалення, призупинення. Повторення живе в ReviewSession.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Check,
  ChevronDown,
  Edit3,
  Layers,
  Loader2,
  PauseCircle,
  PlayCircle,
  Plus,
  Search,
  Trash2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react'

import { Modal, Field, TagPill, Alert, Empty, Loading, Segmented, Badge } from './ui.jsx'
import RichText from './RichText.jsx'
import { api } from '../lib/api.js'
import { SUBJECTS, STATE_LABELS } from '../lib/constants.js'
import { normalizeTag, splitTagList } from '../lib/tags.js'
import { speak, stop as stopSpeaking, isSupported as speechSupported } from '../lib/speech.js'
import { formatInterval } from '../lib/scheduler.js'

const FILTERS = [
  { value: 'all', label: 'Усі' },
  { value: 'now', label: 'До повторення' },
  { value: 'new', label: 'Нові' },
  { value: 'learned', label: 'Вивчені' },
]

const emptyDraft = () => ({ id: null, front: '', back: '', hint: '', tags: [], deck_id: null, image_url: '', back_image_url: '' })

export default function CardBrowser({ decks = [], settings, subject: initialSubject = 'all', onChanged }) {
  const [cards, setCards] = useState([])
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [deckId, setDeckId] = useState(null)
  const [subject, setSubject] = useState(initialSubject)
  const [filter, setFilter] = useState('all')
  const [tag, setTag] = useState(null)
  const [order, setOrder] = useState('created_at')
  const [expanded, setExpanded] = useState(null)
  const [editing, setEditing] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [speakingId, setSpeakingId] = useState(null)
  const [speechError, setSpeechError] = useState(null)
  const abortRef = useRef(null)

  // Пошук із затримкою, щоб не смикати D1 на кожну літеру
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  // Предмет із шапки — джерело фільтра, а не захардкожена математика.
  useEffect(() => {
    setSubject(initialSubject)
  }, [initialSubject])

  const load = useCallback(async () => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    setLoading(true)
    setError(null)
    try {
      const list = await api.listCards({
        q: debounced || undefined,
        deckId: deckId || undefined,
        subject: subject === 'all' ? undefined : subject,
        due: filter === 'all' ? undefined : filter,
        tag: tag || undefined,
        order,
        limit: 500,
      }, { signal: ctrl.signal })
      setCards(list)
      onChanged?.()
    } catch (err) {
      if (err?.status !== 0) setError(err?.message ?? 'Не вдалося завантажити картки.')
    } finally {
      setLoading(false)
    }
  }, [debounced, deckId, subject, filter, tag, order, onChanged])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    api.listTags().then(setTags).catch(() => {})
  }, [])

  useEffect(() => () => stopSpeaking(), [])

  const activeFilters = useMemo(
    () => [deckId, subject !== 'all' ? subject : null, filter !== 'all' ? filter : null, tag, debounced].filter(Boolean).length,
    [deckId, subject, filter, tag, debounced],
  )

  const reset = () => {
    setSearch('')
    setDeckId(null)
    setSubject('all')
    setFilter('all')
    setTag(null)
  }

  const speakCard = (card) => {
    if (speakingId === card.id) {
      stopSpeaking()
      setSpeakingId(null)
      return
    }
    setSpeakingId(card.id)
    setSpeechError(null)
    speak(`${card.front} ${card.back}`, {
      engine: settings?.ttsEngine ?? 'google',
      rate: settings?.speechRate ?? 0.9,
      voiceName: settings?.speechVoice ?? '',
      onEnd: () => {
        setSpeakingId(null)
        setSpeechError(null)
      },
      onError: (err) => {
        setSpeakingId(null)
        setSpeechError(err?.message ?? 'Не вдалося відтворити озвучку.')
      },
    })
  }

  const toggleSuspend = async (card) => {
    setBusyId(card.id)
    try {
      await api.suspendCard(card.id, !card.suspended)
      await load()
    } catch (err) {
      setError(err?.message ?? 'Не вдалося змінити статус картки.')
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (card) => {
    if (!confirm(`Видалити картку?\n\n${card.front.slice(0, 120)}`)) return
    setBusyId(card.id)
    try {
      await api.deleteCard(card.id)
      await load()
    } catch (err) {
      setError(err?.message ?? 'Не вдалося видалити картку.')
    } finally {
      setBusyId(null)
    }
  }

  const save = async (draft) => {
    const payload = {
      front: draft.front.trim(),
      back: draft.back.trim(),
      hint: draft.hint.trim(),
      tags: draft.tags,
      deck_id: draft.deck_id || null,
      image_url: draft.image_url || '',
      back_image_url: draft.back_image_url || '',
    }
    if (payload.front === '' || payload.back === '') {
      throw new Error('Питання і відповідь обовʼязкові.')
    }
    if (draft.id) await api.updateCard({ id: draft.id, ...payload })
    else await api.createCard(payload)
    await load()
  }

  return (
    <div className="cards-workspace space-y-5">
      {/* Фільтри */}
      <div className={`cards-filter-panel ${activeFilters > 0 ? 'has-active' : ''}`}>
        <div className="section-kicker">Пошук та фільтри</div>
        <div className="filter-bar">
          <div className={`search-field ${search ? 'has-query' : ''}`}>
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                className="input !pl-10"
                placeholder="Шукайте за питанням, відповіддю або тегом…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Пошук карток"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-200"
                  aria-label="Очистити пошук"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <select className="input w-auto" value={deckId ?? ''} onChange={(e) => setDeckId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Усі колоди</option>
            {decks.map((d) => (
              <option key={d.id} value={d.id}>
                {d.title} ({d.card_count})
              </option>
            ))}
          </select>

          <select className="input w-auto" value={subject} onChange={(e) => setSubject(e.target.value)}>
            <option value="all">Усі предмети</option>
            {SUBJECTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>

          <select className="input w-auto" value={order} onChange={(e) => setOrder(e.target.value)}>
            <option value="created_at">Спочатку нові</option>
            <option value="due">За терміном</option>
            <option value="alpha">За алфавітом</option>
            <option value="hardest">Найважчі</option>
          </select>

          <button type="button" onClick={() => setEditing(emptyDraft())} className="btn-primary">
            <Plus className="h-4 w-4" /> Картка
          </button>
        </div>

        <div className="filter-row">
          <Segmented value={filter} onChange={setFilter} options={FILTERS} />

          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {tags.slice(0, 12).map((t) => (
                <TagPill
                  key={t.id}
                  name={t.name}
                  count={t.card_count}
                  active={tag === t.name}
                  onClick={() => setTag((prev) => (prev === t.name ? null : t.name))}
                />
              ))}
            </div>
          )}

          {activeFilters > 0 && (
            <button type="button" onClick={reset} className="btn-ghost ml-auto text-xs">
              <X className="h-3.5 w-3.5" /> скинути фільтри ({activeFilters})
            </button>
          )}
        </div>
      </div>

      {error && <Alert tone="error" title="Помилка">{error}</Alert>}

      {loading ? (
        <Loading label="Завантажую картки…" />
      ) : !cards.length ? (
        <Empty
          icon={Layers}
          title={activeFilters ? 'Нічого не знайдено' : 'У базі ще немає карток'}
          action={
            activeFilters ? (
              <button type="button" onClick={reset} className="btn-outline">
                Скинути фільтри
              </button>
            ) : (
              <button type="button" onClick={() => setEditing(emptyDraft())} className="btn-primary">
                <Plus className="h-4 w-4" /> Створити першу картку
              </button>
            )
          }
        >
          {activeFilters
            ? 'Спробуйте інший запит або зніміть фільтри.'
            : 'Згенеруйте картки з конспекту на вкладці «База знань» або створіть першу вручну.'}
        </Empty>
      ) : (
        <>
          <p className="result-ledger">
            <span>Результат</span><b>{cards.length}</b><span>карток</span>
          </p>

          <ul className="space-y-2">
            {cards.map((card) => {
              const isOpen = expanded === card.id
              const state = STATE_LABELS[card.state] ?? STATE_LABELS[0]
              return (
                <li key={card.id} className={`data-row ${card.suspended ? 'opacity-60' : ''}`}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : card.id)}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-slate-50 dark:hover:bg-slate-950/50"
                  >
                    <ChevronDown
                      className={`mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                    <div className="min-w-0 flex-1">
                      <RichText className="line-clamp-2 text-[15px] font-medium">{card.front}</RichText>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <Badge className={state.color}>{state.label}</Badge>
                        {card.deck_title && <Badge>{card.deck_title}</Badge>}
                        {splitTagList(card.tag_list).slice(0, 3).map((t) => (
                          <span key={t} className="chip">
                            #{t}
                          </span>
                        ))}
                        {card.interval > 0 && (
                          <span className="text-xs text-slate-400 tabular-nums">
                            інтервал {formatInterval(card.interval)}
                          </span>
                        )}
                        {card.lapses > 0 && (
                          <span className="text-xs text-rose-400">забувань: {card.lapses}</span>
                        )}
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="animate-card-in space-y-3 border-t border-slate-200 px-4 py-3 dark:border-slate-800">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <p className="label">Питання</p>
                          <RichText className="text-sm">{card.front}</RichText>
                        </div>
                        <div>
                          <p className="label">Відповідь</p>
                          <RichText className="text-sm" tags={splitTagList(card.tag_list)}>
                            {card.back}
                          </RichText>
                        </div>
                      </div>

                      {card.hint && (
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                          <b>Підказка:</b> {card.hint}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-1.5">
                        {speechSupported(settings?.ttsEngine ?? 'google') && (
                          <button
                            type="button"
                            onClick={() => speakCard(card)}
                            className={`btn-ghost !px-2 !py-1 ${speakingId === card.id ? 'is-speaking animate-speak text-white' : ''}`}
                            title={speakingId === card.id ? 'Зупинити' : 'Прослухати'}
                            aria-label={speakingId === card.id ? 'Зупинити озвучку' : 'Прослухати картку'}
                          >
                            {speakingId === card.id ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditing({ ...card, tags: splitTagList(card.tag_list) })}
                          className="btn-ghost !px-2 !py-1"
                          title="Редагувати"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleSuspend(card)}
                          disabled={busyId === card.id}
                          className="btn-ghost !px-2 !py-1"
                          title={card.suspended ? 'Відновити' : 'Призупинити'}
                        >
                          {busyId === card.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : card.suspended ? (
                            <PlayCircle className="h-4 w-4" />
                          ) : (
                            <PauseCircle className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(card)}
                          disabled={busyId === card.id}
                          className="btn-ghost !px-2 !py-1 text-rose-600 dark:text-rose-400"
                          title="Видалити"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <span className="ml-auto text-xs text-slate-400">
                          {card.source === 'gemini' ? 'Gemini' : 'вручну'} · {card.created_at?.slice(0, 10)}
                        </span>
                        {speakingId === card.id && speechError && (
                          <span role="alert" className="w-full text-xs text-rose-300">
                            {speechError}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}

      {editing && (
        <CardEditor
          draft={editing}
          decks={decks}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function CardEditor({ draft: initial, decks, onClose, onSave }) {
  const [draft, setDraft] = useState(initial)
  const [tagInput, setTagInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }))

  const commitTag = () => {
    const t = normalizeTag(tagInput)
    if (t && !draft.tags.includes(t)) set({ tags: [...draft.tags, t] })
    setTagInput('')
  }

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      await onSave(draft)
      onClose()
    } catch (err) {
      setError(err?.message ?? 'Не вдалося зберегти.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={initial.id ? 'Редагувати картку' : 'Нова картка'}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost" disabled={saving}>
            Скасувати
          </button>
          <button type="button" onClick={submit} className="btn-primary" disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Зберегти
          </button>
        </>
      }
    >
      {error && <Alert tone="error" className="mb-3">{error}</Alert>}

      <div className="space-y-3">
        <Field label="Питання" required>
          <textarea
            className="input min-h-20 resize-y"
            value={draft.front}
            onChange={(e) => set({ front: e.target.value })}
          />
        </Field>
        <Field label="Відповідь" required>
          <textarea
            className="input min-h-20 resize-y"
            value={draft.back}
            onChange={(e) => set({ back: e.target.value })}
          />
        </Field>
        <Field label="Підказка">
          <input className="input" value={draft.hint ?? ''} onChange={(e) => set({ hint: e.target.value })} />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Колода">
            <select
              className="input"
              value={draft.deck_id ?? ''}
              onChange={(e) => set({ deck_id: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">— без колоди —</option>
              {decks.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Теги" hint="Enter або кома — додати">
            <div className="flex flex-wrap items-center gap-1.5">
              {draft.tags.map((t) => (
                <TagPill key={t} name={t} onRemove={() => set({ tags: draft.tags.filter((x) => x !== t) })} />
              ))}
              <input
                className="input !py-1 text-xs"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault()
                    commitTag()
                  }
                }}
                placeholder="додати тег…"
              />
            </div>
          </Field>
        </div>
      </div>
    </Modal>
  )
}
